import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Key, Plus } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import AdminLayout from '../../../../layouts/AdminLayout';
import apiService from '../../../../services/api.service';
import ApiKeysTable from './components/ApiKeysTable';
import CreateApiKeyDialog from './components/CreateApiKeyDialog';

function AdminApplicationsPageV3({ title }) {
  const [apiKeysData, setApiKeysData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'expirationDate', direction: 'asc' });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      const sortParam = sortConfig.direction === 'asc' ? sortConfig.key : `-${sortConfig.key}`;

      const response = await apiService.admin.getApiKeys({
        params: {
          skip: currentPage * rowsPerPage,
          limit: rowsPerPage,
          sort: sortParam,
        },
      });

      setApiKeysData(response.paginatedResult);
      setTotalCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, sortConfig]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleSort = (columnId) => {
    setSortConfig((prev) => ({
      key: columnId,
      direction: prev.key === columnId && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
  };

  const handleApiKeyCreated = () => {
    setIsCreateDialogOpen(false);
    fetchApiKeys();
  };

  const handleApiKeyUpdated = () => {
    fetchApiKeys();
  };

  const handleApiKeyDeleted = () => {
    fetchApiKeys();
  };

  return (
    <AdminLayout title={title}>
      <div className="p-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Keys
              </CardTitle>
              <CardDescription>Manage API keys for external application access</CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <ApiKeysTable
              apiKeys={apiKeysData}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              sortConfig={sortConfig}
              onSort={handleSort}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onApiKeyUpdated={handleApiKeyUpdated}
              onApiKeyDeleted={handleApiKeyDeleted}
            />
          </CardContent>
        </Card>

        <CreateApiKeyDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onApiKeyCreated={handleApiKeyCreated}
        />
      </div>
    </AdminLayout>
  );
}

AdminApplicationsPageV3.propTypes = {
  title: PropTypes.string.isRequired,
};

export default AdminApplicationsPageV3;
